import React, { useCallback, useEffect, useState } from 'react'
import { usePlanLapse } from '@/hooks/usePlanLapse'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Play, Pause,
  Rocket, CircleNotch, Calendar, Clock, ChartBar, CheckCircle, Users, Envelope,
  PaperPlaneTilt, EnvelopeOpen, CursorClick, ArrowBendUpLeft, Info, WarningCircle,
  ArrowsClockwise
} from '@phosphor-icons/react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCampaignStore } from '@/stores/campaignStore'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { campaignStatusMeta, campaignBlockingBannerHeading } from '@/lib/campaignStatus'
import { campaignsApi } from '@/lib/api'
import { openEventStream } from '@/lib/sse'
import type { CampaignStage, SentEmailItem } from '@/types/campaign'
import CampaignWorkflowFlow from './CampaignWorkflowFlow'
import RejectionBanner from '@/components/approvals/RejectionBanner'

// How many send-time pills to show per contact before collapsing the rest.
const MAX_SENT_PILLS = 5

// A failed Send Email step for a contact: a short reason for the pill label and
// the full raw provider message for the hover tooltip.
type RecipientFailure = { reason: string; message?: string | null }

// Per-recipient Send Email outcomes: one indigo pill per email actually sent
// (chronological, collapsing beyond MAX_SENT_PILLS into a muted "+N"), plus a
// red pill per failed send showing the short reason (full message on hover).
// Failures are shown first so a problem is the first thing the eye lands on.
const RecipientPills: React.FC<{ times: string[]; failures?: RecipientFailure[] }> = ({
  times,
  failures = [],
}) => {
  if (times.length === 0 && failures.length === 0) {
    return <span className="text-xs text-muted-foreground/60">—</span>
  }
  const shown = times.slice(0, MAX_SENT_PILLS)
  const rest = times.slice(MAX_SENT_PILLS)
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0 max-w-[70%]">
      {failures.map((f, i) => (
        <span
          key={`fail-${i}`}
          title={f.message || f.reason}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-500/10 text-red-600 dark:bg-red-500/[0.15] dark:text-[#f87171] max-w-[220px]"
        >
          <WarningCircle weight="fill" className="w-3 h-3 shrink-0" />
          <span className="truncate">{f.reason}</span>
        </span>
      ))}
      {shown.map((iso, i) => (
        <span
          key={i}
          title={`Email sent ${format(new Date(iso), 'PPpp')}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.15] dark:text-[#a5b4fc]"
        >
          <PaperPlaneTilt weight="fill" className="w-3 h-3" />
          {format(new Date(iso), 'MMM d, h:mm a')}
        </span>
      ))}
      {rest.length > 0 && (
        <span
          title={rest.map(r => format(new Date(r), 'PPpp')).join('\n')}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-muted/70 text-muted-foreground dark:bg-white/[0.06] dark:text-[#8a8f98]"
        >
          +{rest.length}
        </span>
      )}
    </div>
  )
}

const CampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    currentCampaign: campaign,
    isLoading,
    fetchCampaignById,
    setCurrentCampaign,
    launchCampaign,
    pauseCampaign,
    resumeCampaign,
  } = useCampaignStore()

  const [stages, setStages] = useState<CampaignStage[]>([])

  // Per-recipient "sent at" pills, shown for every campaign.
  const showSentPills = true
  // contact_id -> sorted send times (ISO) of emails actually sent to that contact.
  const [sentByContact, setSentByContact] = useState<Record<number, string[]>>({})
  // contact_id -> failed Send Email steps for that contact (short reason + raw message).
  const [failuresByContact, setFailuresByContact] = useState<Record<number, RecipientFailure[]>>({})

  const [loadError, setLoadError] = useState<number | 'unknown' | null>(null)
  // Starting a send ahead of schedule cannot be undone, so it goes through
  // ConfirmDialog (§16) rather than firing on a single click.
  const [launchOpen, setLaunchOpen] = useState(false)
  const { lapsed: planLapsed, reason: lapsedTitle } = usePlanLapse()
  const [isLaunching, setIsLaunching] = useState(false)

  const load = useCallback(() => {
    if (!id) return
    setLoadError(null)
    fetchCampaignById(Number(id)).catch((err: any) => {
      setLoadError(err?.response?.status ?? 'unknown')
    })
  }, [id])

  useEffect(() => {
    if (!id) return
    // Drop a previously-viewed campaign so a failed load can't leave the old
    // one's metrics on screen under a new id.
    const existing = useCampaignStore.getState().currentCampaign
    if (existing && existing.id !== Number(id)) setCurrentCampaign(null)
    load()
  }, [id, load])

  // Group raw sent-email rows into per-contact pills — shared by the SSE
  // payload handler below and the polling fallback, so both apply the same
  // shape to state.
  const applySentEmailRows = (rows: SentEmailItem[]) => {
    const grouped: Record<number, string[]> = {}
    const failed: Record<number, RecipientFailure[]> = {}
    for (const row of rows) {
      if (row.status === 'failed') {
        ;(failed[row.contact_id] ??= []).push({
          reason: row.error_reason || 'Send failed',
          message: row.error_message,
        })
        continue
      }
      if (!row.sent_at) continue
      ;(grouped[row.contact_id] ??= []).push(row.sent_at)
    }
    // Chronological order (ISO strings sort lexicographically by time).
    for (const arr of Object.values(grouped)) arr.sort()
    setSentByContact(grouped)
    setFailuresByContact(failed)
  }

  // Initial load of stage progress and the sent-email pills, on mount, for ANY
  // status. The live stream below only runs while the campaign is running or
  // scheduled, and it is the only other thing that fills these in — so without
  // this fetch a campaign that is paused, completed, or failed renders its
  // workflow with every node showing 0 and no recipient pills at all, which is
  // most campaign pages most of the time. (The polling this replaced did the
  // same one-shot load unconditionally and only *polled* on status.)
  useEffect(() => {
    if (!id) return
    let cancelled = false
    campaignsApi.getStageProgress(Number(id))
      .then(res => { if (!cancelled) setStages(res.data.stages) })
      .catch(() => {})
    campaignsApi.getSentEmails(Number(id))
      .then(res => { if (!cancelled) applySentEmailRows(res.data as SentEmailItem[]) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [id])

  // Live updates: the campaign object (status/metrics), stage progress, and
  // sent-email pills used to be three independent setInterval(10s) polls —
  // every viewer's browser re-fetched all three every 10s regardless of
  // whether anything had changed. Replaced with one SSE stream (backend:
  // GET /campaigns/{id}/events) that pushes a combined snapshot only when a
  // node actually completes, debounced server-side. Falls back to the exact
  // old 3x-polling behavior if the stream can't be used this session (Redis
  // unavailable, a proxy that blocks streaming, etc) so live updates degrade
  // gracefully rather than silently stop.
  useEffect(() => {
    if (!id) return
    if (campaign?.status !== 'running' && campaign?.status !== 'scheduled') return

    let usingFallbackPolling = false
    let fallbackTimer: ReturnType<typeof setInterval> | undefined
    let cancelled = false

    const startFallbackPolling = () => {
      if (usingFallbackPolling || cancelled) return
      usingFallbackPolling = true
      const loadAll = () => {
        campaignsApi.getById(Number(id)).then(res => setCurrentCampaign(res.data)).catch(() => {})
        campaignsApi.getStageProgress(Number(id)).then(res => setStages(res.data.stages)).catch(() => {})
        campaignsApi.getSentEmails(Number(id)).then(res => applySentEmailRows(res.data as SentEmailItem[])).catch(() => {})
      }
      loadAll()
      fallbackTimer = setInterval(loadAll, 10000)
    }

    const closeStream = openEventStream(`/campaigns/${id}/events`, {
      onMessage: (data) => {
        if (cancelled || data?.deleted) return
        if (data.campaign) setCurrentCampaign(data.campaign)
        if (data.stage_progress) setStages(data.stage_progress.stages)
        if (data.sent_emails) applySentEmailRows(data.sent_emails)
      },
      onError: startFallbackPolling,
    })

    return () => {
      cancelled = true
      closeStream()
      if (fallbackTimer) clearInterval(fallbackTimer)
    }
  }, [id, campaign?.status])

  // Each store action toasts its own failure and then rethrows, so every call
  // site has to catch — otherwise the rejection goes unhandled and the button
  // is left looking as though the transition succeeded.
  const confirmLaunch = async () => {
    if (!campaign) return
    setIsLaunching(true)
    try {
      await launchCampaign(campaign.id)
      await fetchCampaignById(campaign.id)
      setLaunchOpen(false)
    } catch {
      // store already toasted the backend's reason
    } finally {
      setIsLaunching(false)
    }
  }

  const handlePause = async () => {
    if (!campaign) return
    try {
      await pauseCampaign(campaign.id)
      await fetchCampaignById(campaign.id)
    } catch {
      // store already toasted
    }
  }

  const handleResume = async () => {
    if (!campaign) return
    try {
      await resumeCampaign(campaign.id)
      await fetchCampaignById(campaign.id)
    } catch {
      // store already toasted
    }
  }

  if (loadError !== null && !campaign) {
    const notFound = loadError === 404
    return (
      <div className="space-y-5">
        <button
          onClick={() => navigate('/campaigns')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground dark:text-[#8a8f98] hover:text-foreground dark:hover:text-[#f7f8f8] transition-all duration-150"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Campaigns
        </button>
        <Card variant="default" className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]">
          <CardContent className="p-14 text-center">
            <div className="max-w-md mx-auto" role="alert" aria-live="polite">
              <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mx-auto mb-4">
                <WarningCircle className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
              </div>
              <h3 className="text-foreground dark:text-[#f7f8f8] mb-2" style={{ fontSize: '20px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}>
                {notFound ? 'Campaign not found' : "Couldn't load this campaign"}
              </h3>
              <p className="text-muted-foreground dark:text-[#8a8f98] mb-6 leading-relaxed" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                {notFound
                  ? 'It may have been deleted, or the link points at a campaign on another account.'
                  : 'The request failed before the campaign could load. Nothing has changed on the campaign itself.'}
              </p>
              <div className="flex items-center justify-center gap-2">
                {!notFound && (
                  <Button variant="default" size="sm" icon={<ArrowsClockwise className="h-4 w-4" weight="regular" />} onClick={load}>
                    Try again
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => navigate('/campaigns')}>
                  Back to campaigns
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading || !campaign) {
    return (
      <div className="flex items-center justify-center py-32">
        <CircleNotch className="w-8 h-8 animate-spin text-[#5e6ad2] dark:text-[#a5b4fc]" />
      </div>
    )
  }

  const metrics = [
    { label: 'Recipients', value: campaign.total_recipients, icon: Users },
    { label: 'Sent', value: campaign.emails_sent, icon: PaperPlaneTilt },
    { label: 'Delivered', value: campaign.emails_delivered, icon: CheckCircle },
    { label: 'Opened', value: campaign.emails_opened, icon: EnvelopeOpen, suffix: `${campaign.open_rate.toFixed(1)}%` },
    { label: 'Clicked', value: campaign.emails_clicked, icon: CursorClick, suffix: `${campaign.click_rate.toFixed(1)}%` },
    { label: 'Replied', value: campaign.emails_replied, icon: ArrowBendUpLeft, suffix: `${campaign.reply_rate.toFixed(1)}%` },
  ]

  // Campaign lifecycle milestones — rendered as a vertical timeline, in
  // chronological order. Only present timestamps are shown. The terminal
  // "Completed" node reads emerald (success); the rest carry the brand indigo.
  const timeline = [
    campaign.created_at && { label: 'Created', icon: Clock, date: campaign.created_at, tone: 'brand' as const },
    campaign.scheduled_start && { label: 'Scheduled Start', icon: Calendar, date: campaign.scheduled_start, tone: 'brand' as const },
    campaign.actual_start && { label: 'Actual Start', icon: Play, date: campaign.actual_start, tone: 'brand' as const },
    campaign.actual_completion && { label: 'Completed', icon: CheckCircle, date: campaign.actual_completion, tone: 'success' as const },
  ].filter(Boolean) as { label: string; icon: React.ComponentType<{ className?: string; weight?: string }>; date: string; tone: 'brand' | 'success' }[]

  const toneMeta = {
    brand: {
      ring: 'bg-[#5e6ad2]/10 ring-[#5e6ad2]/20 dark:bg-[#5e6ad2]/[0.12] dark:ring-[#a5b4fc]/20',
      icon: 'text-[#5e6ad2] dark:text-[#a5b4fc]',
    },
    success: {
      ring: 'bg-emerald-50 ring-emerald-500/20 dark:bg-emerald-500/10 dark:ring-emerald-400/20',
      icon: 'text-emerald-600 dark:text-emerald-400',
    },
  }

  const status = campaignStatusMeta(campaign.status)
  const blockingHeading = campaign.last_error_reason ? campaignBlockingBannerHeading(status.key) : null
  // Reuses the red/amber pill-banner tones already established for the
  // per-stage "failed recipients" / "held" banners in CampaignWorkflowFlow.
  const blockingBannerClass =
    status.variant === 'destructive'
      ? 'bg-red-50 text-red-700 ring-1 ring-red-500/20 dark:bg-red-500/10 dark:text-[#f87171] dark:ring-red-400/20'
      : 'bg-amber-50 text-amber-700 ring-1 ring-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-400/20'

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/campaigns')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground dark:text-[#8a8f98] hover:text-foreground dark:hover:text-[#f7f8f8] transition-all duration-150 mb-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Campaigns
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1
              className="text-foreground dark:text-[#f7f8f8]"
              style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
            >
              {campaign.name}
            </h1>
            <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium', status.chipClass)}>
              <status.Icon className={cn('h-3 w-3', status.iconNudge)} weight="fill" />
              {status.label}
            </span>
            {campaign.workflow_name && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted/60 dark:bg-white/[0.04] text-muted-foreground dark:text-[#8a8f98]">
                {campaign.workflow_name}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {campaign.status === 'scheduled' && (
              <Button
                variant="default"
                icon={<Rocket className="w-4 h-4" />}
                onClick={() => setLaunchOpen(true)}
                disabled={planLapsed}
                title={lapsedTitle}
              >
                Launch now
              </Button>
            )}
            {campaign.status === 'running' && (
              <Button
                variant="outline"
                icon={<Pause className="w-4 h-4" />}
                onClick={handlePause}
              >
                Pause
              </Button>
            )}
            {campaign.status === 'paused' && (
              <Button
                variant="default"
                icon={<Play className="w-4 h-4" />}
                onClick={handleResume}
                disabled={planLapsed}
                title={lapsedTitle}
              >
                Resume
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Who rejected the launch and why, so the Rep knows what to fix before
          sending it again (spec 5.8). Renders nothing unless rejected. */}
      <RejectionBanner approval={campaign.approval} className="mb-4" />

      {/* Blocking reason banner — the campaign-level counterpart to the
          per-recipient failure pills below: surfaces `error_log` (plan
          gates, credit limits, hard failures) which previously wasn't
          rendered anywhere on this page. */}
      {blockingHeading && campaign.last_error_reason && (
        <div className={cn('flex items-start gap-2.5 rounded-lg px-4 py-3', blockingBannerClass)}>
          <WarningCircle weight="fill" className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p style={{ fontSize: '13px', fontWeight: 590 }}>{blockingHeading}</p>
            <p className="mt-0.5 opacity-90" style={{ fontSize: '13px', fontWeight: 450 }}>
              {campaign.last_error_reason}
            </p>
          </div>
        </div>
      )}

      {/* Metrics Grid */}
      <Card
        className="relative overflow-hidden rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]"
        style={{ backgroundImage: 'radial-gradient(circle at top right, rgba(94,106,210,0.10), transparent 60%)' }}
      >
        <CardContent className="relative p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {metrics.map(metric => (
              <div
                key={metric.label}
                className="rounded-lg border border-border/50 dark:border-white/[0.08] bg-background/60 dark:bg-white/[0.02] p-4"
              >
                <div className="flex items-center gap-2 mb-2.5">
                  <div
                    className="h-6 w-6 rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] flex items-center justify-center flex-shrink-0"
                    aria-hidden="true"
                  >
                    <metric.icon className="h-3.5 w-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
                  </div>
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98]"
                    style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                  >
                    {metric.label}
                  </p>
                </div>
                <p
                  className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                  style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.4px', lineHeight: 1.1 }}
                >
                  {metric.value.toLocaleString()}
                </p>
                {metric.suffix && (
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 tabular-nums"
                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                  >
                    {metric.suffix}
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Workflow Stages */}
      {campaign.workflow_id && (
        <div>
          <Card variant="default" className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] p-1.5">
                    <ChartBar className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                  </div>
                  <h2
                    className="text-foreground dark:text-[#f7f8f8]"
                    style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.3 }}
                  >
                    Workflow stages
                  </h2>
                </div>
                {stages.length > 0 && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted/60 dark:bg-white/[0.04] text-muted-foreground dark:text-[#8a8f98]">
                    {stages.length} {stages.length === 1 ? 'stage' : 'stages'}
                  </span>
                )}
              </div>

              <CampaignWorkflowFlow workflowId={campaign.workflow_id} stages={stages} />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recipients */}
      {campaign.target_contacts && campaign.target_contacts.length > 0 && (
        <div id="campaign-recipients" className="scroll-mt-24">
          <Card variant="default" className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] p-1.5">
                    <Users className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" />
                  </div>
                  <h2
                    className="text-foreground dark:text-[#f7f8f8]"
                    style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.3 }}
                  >
                    Recipients
                  </h2>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted/60 dark:bg-white/[0.04] text-muted-foreground dark:text-[#8a8f98]">
                  {campaign.target_contacts.length.toLocaleString()}
                </span>
              </div>
              <div className="rounded-lg border border-border/50 dark:border-white/[0.08] divide-y divide-border/50 dark:divide-white/[0.06]">
                {campaign.target_contacts.map(contact => (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between gap-4 px-4 py-2.5 hover:bg-muted/20 dark:hover:bg-white/[0.03] transition-colors duration-150"
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-foreground dark:text-[#f7f8f8] truncate"
                        style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                      >
                        {contact.name || contact.email}
                        {contact.is_deleted && (
                          <span className="ml-2 inline-flex items-center align-middle rounded-full px-2 py-0.5 text-[11px] font-medium bg-muted/60 dark:bg-white/[0.04] text-muted-foreground dark:text-[#8a8f98]">
                            Deleted
                          </span>
                        )}
                      </p>
                      {contact.name && (
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] truncate"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          {contact.email}
                        </p>
                      )}
                    </div>
                    {showSentPills ? (
                      <RecipientPills
                        times={sentByContact[contact.id] ?? []}
                        failures={failuresByContact[contact.id] ?? []}
                      />
                    ) : (
                      <Envelope className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaign Details */}
      <div>
        <Card variant="default" className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]">
          <CardContent className="p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <div className="rounded-md bg-[#5e6ad2]/10 dark:bg-[#5e6ad2]/[0.12] p-1.5">
                <Info className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
              </div>
              <h2
                className="text-foreground dark:text-[#f7f8f8]"
                style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.3 }}
              >
                Campaign Details
              </h2>
            </div>

            {campaign.description && (
              <div className="rounded-lg bg-muted/30 dark:bg-white/[0.03] p-4 mb-6">
                <p
                  className="uppercase text-muted-foreground/70 dark:text-[#62666d] mb-1.5"
                  style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.09em' }}
                >
                  Description
                </p>
                <p
                  className="text-foreground dark:text-[#f7f8f8] leading-relaxed"
                  style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
                >
                  {campaign.description}
                </p>
              </div>
            )}

            {/* Lifecycle timeline */}
            <div>
              {timeline.map((m, i) => {
                const meta = toneMeta[m.tone]
                const isLast = i === timeline.length - 1
                return (
                  <div key={m.label} className="flex gap-3.5">
                    {/* Rail: medallion + connector */}
                    <div className="flex flex-col items-center">
                      <div className={cn('flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ring-1', meta.ring)}>
                        <m.icon className={cn('h-4 w-4', meta.icon)} weight="bold" />
                      </div>
                      {!isLast && <div className="my-1 w-px flex-1 rounded-full bg-border/60 dark:bg-white/[0.08]" />}
                    </div>
                    {/* Milestone */}
                    <div className={cn('min-w-0 flex-1', isLast ? 'pb-0' : 'pb-5')}>
                      <p
                        className="uppercase text-muted-foreground/70 dark:text-[#62666d]"
                        style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.09em' }}
                      >
                        {m.label}
                      </p>
                      <p
                        className="mt-1 text-foreground dark:text-[#f7f8f8] tabular-nums"
                        style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}
                      >
                        {format(new Date(m.date), 'PPpp')}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Launch confirm — "Launch now" sends ahead of the scheduled start. */}
      <ConfirmDialog
        open={launchOpen}
        title="Start sending now?"
        description={`"${campaign.name}" will start sending to ${campaign.total_recipients.toLocaleString()} ${campaign.total_recipients === 1 ? 'recipient' : 'recipients'} immediately, ahead of its scheduled start. Sent email cannot be recalled.`}
        confirmLabel="Launch now"
        cancelLabel="Cancel"
        loading={isLaunching}
        onConfirm={confirmLaunch}
        onCancel={() => !isLaunching && setLaunchOpen(false)}
      />
    </div>
  )
}

export default CampaignDetailPage
